import classNames from 'classnames';
import type { Options as FocusTrapOptions } from 'focus-trap';
import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { useSafariFullScreenStylesAdjustments } from '~/Utils/Hooks/useSafariFullScreenStylesAdjustments';
import { ScrollLock } from '~/Utils/ScrollLock/ScrollLock';
import type { Theme } from '~/Utils/Theme';
import styles from './Modal.module.css';
import type { ModalVariant } from './ModalVariant';
import { Portal } from './Portal';

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    variant: ModalVariant;
    withWrapper?: boolean;
    className?: string;
    contentClassName?: string;
    theme?: Pick<Theme, 'withRoundedCorners'>;
}

export const Modal: React.FC<React.PropsWithChildren<ModalProps>> = props => {
    const contextualTheme = useTheme();
    const {
        isOpen,
        onClose,
        children,
        variant,
        withWrapper,
        className,
        contentClassName,
        theme = contextualTheme,
    } = props;

    const isFullScreen = variant === 'fullScreen';
    useSafariFullScreenStylesAdjustments(isOpen, `safariModalFix`, isFullScreen);
    const { withRoundedCorners } = theme;

    const focusTrapOptions: FocusTrapOptions = isFullScreen
        ? {
              escapeDeactivates: false,
          }
        : {
              escapeDeactivates: true,
              onDeactivate: onClose,
          };

    return isOpen ? (
        <Portal>
            <FocusTrap active={isOpen} focusTrapOptions={focusTrapOptions}>
                <div
                    className={classNames(
                        styles.Modal,
                        styles[`Variant--${variant}`],
                        withRoundedCorners && styles['Variant--rounded'],
                        className
                    )}
                >
                    <ScrollLock isLocked={isOpen} shouldHideMainScrollBar={isFullScreen} />
                    <div className={classNames(styles.Inner, withWrapper && styles['Inner--withWrapper'])}>
                        <div className={classNames(styles.Content, contentClassName)}>{children}</div>
                    </div>
                    {!isFullScreen && <div className={styles.CloseUnderlay} onClick={onClose} aria-hidden />}
                </div>
            </FocusTrap>
        </Portal>
    ) : null;
};
